import { supabase } from '../supabase';
import { readPendingShots, removePendingShot, type PendingShot } from '../pendingShots';
import { createQuickSession, clearSessionPlanned } from './rangeLifecycle';
import { packMagazine, reconcileSessionMagazine } from './rangeMagazines';
import { firedExceedsLoaded, isLoadedShortfall } from '../magCloseOut';

/**
 * M70c (E2/E10) — replay for the offline shot queue. Confirm-before-apply:
 * every entry re-checks live state FIRST, so a write that actually landed
 * (or landed from another device) is dropped, never double-counted.
 *
 * Outcomes: 'applied' (wrote now) · 'already' (effect present — dropped) ·
 * 'kept' (still failing; stays for the pending-logs sheet's Retry/Discard).
 */

export type ReplayOutcome = 'applied' | 'already' | 'kept';

interface LineRow {
  id: string;
  range_session_id: string;
  reconciled_at: string | null;
}

/** True for the idempotency-index collision the RPC surfaces on a racing
 *  duplicate (E2) — the effect exists, so the entry is satisfied. */
function isIdemCollision(error: { code?: string } | null): boolean {
  return !!error && error.code === '23505';
}

async function lineFor(sessionId: string, magazineId: string): Promise<LineRow | null> {
  const { data } = await supabase
    .from('range_session_magazines')
    .select('id, range_session_id, reconciled_at')
    .eq('range_session_id', sessionId)
    .eq('magazine_id', magazineId)
    .maybeSingle();
  return (data as LineRow | null) ?? null;
}

/** The set's live loaded total (what the server summed), or null when the
 *  read fails. */
async function loadedFor(magazineId: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('magazine_contents')
    .select('rounds')
    .eq('magazine_id', magazineId);
  if (error || !data) return null;
  return (data as { rounds: number }[]).reduce((n, r) => n + (r.rounds ?? 0), 0);
}

/** Apply one queue entry through the confirm check. Does NOT touch the queue —
 *  callers remove on 'applied'/'already'. */
export async function applyPendingShot(
  entry: PendingShot,
  userId: string,
): Promise<{ outcome: ReplayOutcome; message?: string }> {
  // 1 — pin down the target session.
  let sessionId = entry.session_id ?? null;
  let sessionWasPlanned = false;
  if (sessionId) {
    const { data } = await supabase.from('range_sessions')
      .select('id, is_planned').eq('id', sessionId).maybeSingle();
    if (!data) sessionId = null; // session deleted since enqueue — re-resolve
    else sessionWasPlanned = !!(data as { is_planned: boolean }).is_planned;
  }
  if (!sessionId) {
    const { data: sessions } = await supabase.from('range_sessions')
      .select('id, is_planned, created_at')
      .eq('date', entry.date)
      .order('created_at', { ascending: false });
    const pick = [...((sessions as { id: string; is_planned: boolean; created_at: string }[] | null) ?? [])]
      .sort((a, b) => {
        if (a.is_planned !== b.is_planned) return a.is_planned ? -1 : 1;
        return b.created_at.localeCompare(a.created_at);
      })[0];
    if (pick) { sessionId = pick.id; sessionWasPlanned = pick.is_planned; }
  }
  if (!sessionId) {
    const { data, error } = await createQuickSession(userId, { date: entry.date, venueId: entry.venue_id });
    if (error || !data) return { outcome: 'kept', message: error?.message ?? 'Could not create the trip.' };
    sessionId = data.id;
  }

  // 2 — confirm: what does the line look like NOW?
  const existing = await lineFor(sessionId, entry.magazine_id);
  if (existing?.reconciled_at) {
    // Effect present (this write landed after a lost reply, or another device
    // closed it out). Confirm-before-enqueue rule: success, drop the entry.
    return { outcome: 'already' };
  }

  // 3 — apply: pack if needed, then reconcile the open line.
  let lineId = existing?.id ?? null;
  if (!lineId) {
    const { data: mag } = await supabase.from('magazines')
      .select('capacity').eq('id', entry.magazine_id).maybeSingle();
    const cap = (mag as { capacity: number } | null)?.capacity ?? 0;
    const magsTaken = cap > 0 ? Math.max(1, Math.ceil(entry.rounds / cap)) : 1;
    const { error: packErr } = await packMagazine(sessionId, entry.magazine_id, magsTaken);
    if (packErr && !isIdemCollision(packErr)) {
      // UNIQUE(session, magazine) violation ⇒ a line appeared concurrently —
      // fall through to re-read; any other failure keeps the entry.
      if (packErr.code !== '23505') return { outcome: 'kept', message: packErr.message };
    }
    const packed = await lineFor(sessionId, entry.magazine_id);
    if (!packed) return { outcome: 'kept', message: 'Packed line not found after packing.' };
    if (packed.reconciled_at) return { outcome: 'already' };
    lineId = packed.id;
  }

  const { error: recErr } = await reconcileSessionMagazine(lineId, entry.rounds);
  if (recErr) {
    if (isIdemCollision(recErr)) return { outcome: 'already' };
    // M98 (E15) — the server's loaded-shortfall refusal says "group" and is
    // never shown as-is: re-derive the DD6 sentence from the set's live total
    // (the pending-logs sheet renders this message). Any other failure keeps
    // the server's own words.
    if (isLoadedShortfall(recErr.message)) {
      const loaded = await loadedFor(entry.magazine_id);
      const sentence = loaded == null
        ? `Not enough rounds loaded in this set to fire ${entry.rounds.toLocaleString()}.`
        : firedExceedsLoaded(entry.rounds, loaded) ?? recErr.message;
      return { outcome: 'kept', message: sentence };
    }
    return { outcome: 'kept', message: recErr.message };
  }
  if (sessionWasPlanned) await clearSessionPlanned(sessionId); // E8 parity
  return { outcome: 'applied' };
}

let replaying = false;

/** AppShell hook body (E10): drain the queue on mount + 'online'. Sequential,
 *  single-flight; failures stay queued for the pending-logs sheet. */
export async function replayPendingShots(userId: string): Promise<{ applied: number; kept: number }> {
  if (replaying) return { applied: 0, kept: 0 };
  replaying = true;
  let applied = 0;
  let kept = 0;
  try {
    for (const entry of readPendingShots()) {
      const { outcome } = await applyPendingShot(entry, userId);
      if (outcome === 'kept') kept += 1;
      else {
        removePendingShot(entry.id);
        if (outcome === 'applied') applied += 1;
      }
    }
  } finally {
    replaying = false;
  }
  return { applied, kept };
}
